import React from "react";
import CountUp from "react-countup";
import SVGRenderer from "../SVGRenderer";
import LazyImage from "components/elements/LazyImage/LazyImage";
import SmartLink from "components/SmartLink";
import Button from "components/elements/Button/Button";
import { isColorDark } from "helpers/contrastCalculator";

import styles from "./Card.module.scss";

/**
 * Testimonial Card that displays reviews with quotes, author info and ratings
 * Uses standard card props differently:
 * - title: author name
 * - lead: author position/title
 * - desc: testimonial quote
 * - image: author photo
 */
const CardTestimonial = React.forwardRef(({ 
	inView,
	title, // Author name
	svg,
	lead, // Author position
	desc, // Testimonial quote
	cta_text,
	cta_link,
	image, // Author image
	image_alt,
	bg_image,
	bg_image_alt,
	rating,
	bg_color, 
	className = "", 
	theme,
	count_up,
	...otherProps
}, ref) => {
	// Check if background is dark (for button styling)
	const isDarkBg = bg_color ? isColorDark(bg_color.replace(/var\(|\)/g, '').trim()) : false;
	
	return (
		<div
			ref={ref}
			{...(theme ? { "data-bs-theme": theme } : {})}
			style={bg_color && { backgroundColor: bg_color }}
			className={`${styles.CardTestimonial} card h-100 rounded-4 shadow border-0 position-relative overflow-hidden ${className}`}>
			<div className="card-body p-4 d-flex flex-column">
				{/* Quote icon */}
				<div className="quote-marks position-absolute opacity-25 top-0 end-0 me-3 mt-2">
					<i className="bi bi-quote fs-1" aria-hidden="true"></i>
				</div>
				
				{/* Star rating if provided */}
				{rating && (
					<div className="rating mb-3" aria-label={`Rating: ${rating} out of 5 stars`}>
						{/* Generate star icons based on rating value */}
						{[...Array(5)].map((_, i) => (
							<i 
								key={`star-${i}`}
								className={`bi bi-star${i < Math.floor(rating) ? '-fill' : (i < Math.ceil(rating) && rating % 1 >= 0.5 ? '-half' : '')} text-warning me-1`}
								aria-hidden="true"></i>
						))}
						<span className="visually-hidden">{rating} out of 5 stars</span>
					</div>
				)}
				
				{/* Optional SVG */}
				{svg && (
					<div className="mb-3">
						<SVGRenderer svgString={svg} />
					</div>
				)}
				
				{/* Optional CountUp */}
				{inView && count_up && (
					<CountUp
						className="count-up mb-3"
						end={count_up}
						duration={5}
						separator="."
					/>
				)}
				
				{/* Main testimonial quote */}
				{desc && (
					<blockquote className="card-text mb-4 fs-5 position-relative">
						<p
							className="testimonial-text"
							dangerouslySetInnerHTML={{ __html: desc }}></p>
					</blockquote>
				)}
				
				{/* Author information */}
				<div className="testimonial-author d-flex align-items-center mt-auto">
					{image && (
						<div className="author-image me-3" style={{ width: '50px', height: '50px' }}>
							<LazyImage
								src={image}
								alt={`Photo of ${title}`}
								className="rounded-circle"
								fill_mode="cover"
							/>
						</div>
					)}
					<div className="author-info">
						{title && <h4 className="author-name fs-6 fw-bold mb-0">{title}</h4>}
						{lead && <p className="author-title small text-muted mb-0">{lead}</p>}
					</div>
				</div>
				
				{/* Optional CTA button */}
				{cta_text && cta_link && (
					<div className="mt-3">
						<SmartLink 
							to={cta_link}
							title={cta_text}>
							<Button
								size={"sm"}
								variant={isDarkBg ? "light" : "outline-primary"}
								letterCase="uppercase">
								{cta_text}
							</Button>
						</SmartLink>
					</div>
				)}
			</div>
		</div>
	);
});

export default CardTestimonial;
